import {
  createResource,
  deleteResource,
  getResource,
  readUrl,
  throwOnApiError,
  updateResource,
  updateResourceBodyless,
} from "./api";

// The Quotes section pages through every row on the client, so it reads them all: up to the model's
// cap, Id::Quote::MAX_ASSOCIATIONS_ALLOWED. Ordered featured-first then by list_position, passed
// explicitly rather than relying on the view's own `order by list_position`: PostgREST does not
// guarantee a view's internal ordering survives its wrapping query, so the client pins the order it
// actually needs.
const ROWS_LIMIT = 999;

// Read: PostgREST, scoped to one polymorphic owner. `ownerType` must be the CONCRETE STI owner_type the
// rows are persisted with (e.g. "Id::PromoEntity::Expert"), never the abstract "Id::PromoEntity" --
// Rails' polymorphic_name serializes the leaf class, so a query against the base name matches nothing.
const getAllByOwner = async (ownerType, ownerId) => {
  // `featured` is a nullable column (default false, but existing rows can carry a null) --
  // Postgres' plain `desc` sorts nulls first, which would put a null-featured row ABOVE an
  // actually-featured one. `nullslast` pins nulls to the bottom of the featured-desc block instead,
  // alongside `false`.
  const selector = `owner_id=eq.${ownerId}&owner_type=eq.${ownerType}&order=featured.desc.nullslast,list_position.asc`;
  const url = readUrl("quotes", { selector, paginationParams: `limit=${ROWS_LIMIT}` });
  return getResource(url);
};

// Mutations go through the nested Rails routes (Id::Api::QuotesController) -- writes never go through
// PostgREST/Supabase-js. `resource` is the route segment for the owning entity type ("experts",
// "companies", "products" -- see Id::Engine routes' quotable_and_media_itemable concern), kept generic
// so companies/products can adopt this client later; only the Expert profile page wires editing today.
const createForOwner = async (resource, ownerId, attributes) => {
  const url = `/id/api/${resource}/${ownerId}/quotes`;
  // Check for `{ error }` BEFORE unwrapping to attributes -- otherwise a failed create returns
  // `undefined` and the caller can't tell success from failure.
  const response = throwOnApiError(await createResource(url, { data: { attributes } }));
  return response?.data?.data?.attributes;
};

const updateById = async (resource, ownerId, id, attributes) => {
  const url = `/id/api/${resource}/${ownerId}/quotes/${id}`;
  return throwOnApiError(await updateResource(url, { data: { attributes } }));
};

const deleteById = async (resource, ownerId, id) => {
  const url = `/id/api/${resource}/${ownerId}/quotes/${id}`;
  return throwOnApiError(await deleteResource(url));
};

// list_position is the ABSOLUTE target position (1-indexed) within the owner's list, not a delta --
// the server (acts_as_list) renumbers every sibling row that needs to shift, so the caller only has to
// send where this row should land (e.g. its current position -1 for "move up").
const updateListPositionById = async (resource, ownerId, id, listPosition) => {
  const url = `/id/api/${resource}/${ownerId}/quotes/${id}/update_list_position`;
  return throwOnApiError(await updateResource(url, { data: { attributes: { list_position: listPosition } } }));
};

// feature/unfeature are a bodyless PUT pair (FlippableRoute), not a single toggle endpoint -- the
// caller's `featured` boolean selects which of the two routes to call.
const setFeaturedById = async (resource, ownerId, id, featured) => {
  const action = featured ? "feature" : "unfeature";
  const url = `/id/api/${resource}/${ownerId}/quotes/${id}/${action}`;
  return throwOnApiError(await updateResourceBodyless(url));
};

export default { createForOwner, deleteById, getAllByOwner, setFeaturedById, updateById, updateListPositionById };
